/* eslint-disable import/namespace */
import { Formik } from 'formik';
import { useContext, useEffect, useState } from 'react';

import { CustomerEditCsvContext } from './CustomerImportCSV';

import { CloseButton } from '@components/ActionButtons';
import {
  FormSectionContainer,
  FormButtonsContainer,
} from '@components/Container';
import {
  ActionableFormikInput,
  CancelButton,
  FormikCheckbox,
  FormikInput,
  SubmitButton,
} from '@components/Form';
import { Loading } from '@components/Loading';
import { Modal } from '@components/Modal';
import { Space } from '@components/Space';
import { CustomerViewModel } from '@models/Customer';
import {
  shippingAddressDefaultValue,
  shippingAddressValidation,
  ShippingAddressViewModel,
} from '@models/ShippingAddress';
import { CustomerService } from '@services/Customer';

export const CustomerImportCSVEditShippingAddress = () => {
  const { customer, setCustomer, updateCustomerInCsv } = useContext(
    CustomerEditCsvContext
  );
  const [showEditShippingAddress, setShowEditShippingAddress] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const loadCustomerBillingAddress = () => {
    CustomerService.getCustomerBillingAddresses(Number(customer?.id)).then(
      (result) => {
        if (result.isSuccess && 'data' in result) {
          setCustomer((prevState) =>
            prevState
              ? {
                  ...prevState,
                  billingAddresses: result.data,
                }
              : prevState
          );
        }
        setIsLoading(false);
      }
    );
  };

  useEffect(() => {
    loadCustomerBillingAddress();
  }, [customer?.id]);

  const formatAddress = (addr?: ShippingAddressViewModel) => {
    if (!addr) return '';
    return `${addr.address1 || ''} ${addr.address2 || ''}, ${
      addr.city || ''
    }, ${addr.state || ''}, ${addr.country || ''} ${addr.postalCode || ''}`;
  };

  if (isLoading) {
    return <Loading />;
  } else {
    return (
      <>
        <ActionableFormikInput
          label="Shipping Address"
          name="fullShippingAddress"
          onEditAction={() => {
            setShowEditShippingAddress(true);
          }}
          readonly={true}
        />

        {showEditShippingAddress && (
          <Modal show={true}>
            <div className="space-y-4">
              <div className="flex justify-end">
                <CloseButton
                  onClick={() => setShowEditShippingAddress(false)}
                />
              </div>

              <Formik
                initialValues={{
                  ...(customer?.defaultShippingAddress ??
                    shippingAddressDefaultValue),
                  fullShippingAddress: formatAddress(
                    customer?.defaultShippingAddress
                  ),
                }}
                validationSchema={shippingAddressValidation}
                validateOnBlur={true}
                validateOnChange={true}
                onSubmit={(values, actions) => {
                  if (!customer) return;

                  const updatedShippingAddress = {
                    ...customer.defaultShippingAddress,
                    ...values,
                    id: customer.defaultShippingAddress?.id ?? 0,
                  };

                  const updatedCustomer: CustomerViewModel = {
                    ...customer,
                    defaultShippingAddress: updatedShippingAddress,
                    shippingAddresses: customer.shippingAddresses?.map((addr) =>
                      addr.id === updatedShippingAddress.id
                        ? updatedShippingAddress
                        : addr
                    ) ?? [updatedShippingAddress],
                  };

                  setCustomer(updatedCustomer);
                  updateCustomerInCsv(updatedCustomer);

                  setShowEditShippingAddress(false);
                  actions.setSubmitting(false);
                }}
              >
                {(formikProps) => {
                  useEffect(() => {
                    formikProps.setFieldValue(
                      'fullShippingAddress',
                      formatAddress(formikProps.values)
                    );
                  }, [formikProps.values]);

                  return (
                    <>
                      <form method="POST" onSubmit={formikProps.handleSubmit}>
                        <FormSectionContainer>
                          <FormikInput label="Address 1" name="address1" />
                          <FormikInput label="Address 2" name="address2" />
                          <FormikInput label="City" name="city" />
                          <FormikInput label="State" name="state" />
                          <FormikInput label="Country" name="country" />
                          <FormikInput label="Postal" name="postalCode" />
                          <FormikCheckbox label="Is Active" name="isActive" />

                          <Space />
                        </FormSectionContainer>
                        <FormButtonsContainer>
                          <CancelButton
                            onClick={() => setShowEditShippingAddress(false)}
                          />

                          <SubmitButton
                            label="Save"
                            disabled={
                              formikProps.isSubmitting || !formikProps.isValid
                            }
                          />
                        </FormButtonsContainer>
                      </form>
                    </>
                  );
                }}
              </Formik>
            </div>
          </Modal>
        )}
      </>
    );
  }
};
